import React from "react";
import { sanitizeHtml } from "../../lib/sanitize";
import { Box, Text } from "@chakra-ui/react";

// 간단한 마크다운 테이블 파서
const parseMarkdownTable = (text) => {
  const lines = text.trim().split("\n");
  if (lines.length < 2) return null;

  // 헤더 행 확인
  const headerLine = lines[0];
  const separatorLine = lines[1];

  if (!headerLine.includes("|") || !separatorLine.match(/^[\s|:-]+$/)) {
    return null;
  }

  const parseRow = (line) =>
    line
      .split("|")
      .map((cell) => cell.trim())
      .filter((cell) => cell !== "");

  const headers = parseRow(headerLine);
  const rows = lines
    .slice(2)
    .filter((line) => line.includes("|"))
    .map(parseRow);

  return { headers, rows };
};

// 마크다운 텍스트를 렌더링
const renderContent = (content) => {
  if (!content) return null;

  const parts = [];
  const lines = content.split("\n");
  let currentBlock = [];

  const flushText = () => {
    if (currentBlock.length > 0) {
      const text = currentBlock.join("\n");
      parts.push({ type: "text", content: text });
      currentBlock = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const nextLine = lines[i + 1];

    // 테이블 시작 감지
    if (
      line.includes("|") &&
      nextLine &&
      nextLine.match(/^[\s|:-]+$/)
    ) {
      flushText();
      const tableLines = [line];
      let j = i + 1;
      while (j < lines.length && lines[j].includes("|")) {
        tableLines.push(lines[j]);
        j++;
      }
      const table = parseMarkdownTable(tableLines.join("\n"));
      if (table) {
        parts.push({ type: "table", content: table });
        i = j - 1;
        continue;
      }
    }

    currentBlock.push(line);
  }
  flushText();

  return parts.map((part, idx) => {
    if (part.type === "table") {
      return (
        <Box
          key={idx}
          overflowX="auto"
          my={2}
          css={{
            "&::-webkit-scrollbar": { height: "4px" },
            "&::-webkit-scrollbar-thumb": {
              background: "#CBD5E0",
              borderRadius: "2px",
            },
          }}
        >
          <Box
            as="table"
            w="100%"
            fontSize="xs"
            borderCollapse="collapse"
          >
            <Box as="thead">
              <Box as="tr" bg="gray.100">
                {part.content.headers.map((h, hi) => (
                  <Box
                    key={hi}
                    as="th"
                    px={2}
                    py={1}
                    textAlign="left"
                    fontWeight="semibold"
                    borderBottom="2px solid"
                    borderColor="gray.300"
                    whiteSpace="nowrap"
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box as="tbody">
              {part.content.rows.map((row, ri) => (
                <Box
                  key={ri}
                  as="tr"
                  _hover={{ bg: "gray.50" }}
                  borderBottom="1px solid"
                  borderColor="gray.100"
                >
                  {row.map((cell, ci) => (
                    <Box
                      key={ci}
                      as="td"
                      px={2}
                      py={1}
                      whiteSpace="nowrap"
                    >
                      {cell}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      );
    }

    // 텍스트 렌더링 (볼드, 인라인 코드 등 간단한 마크다운 처리)
    return (
      <Text
        key={idx}
        fontSize="sm"
        lineHeight="1.7"
        whiteSpace="pre-wrap"
        sx={{
          "& strong, & b": { fontWeight: "bold" },
        }}
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(part.content
            .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
            .replace(/`(.*?)`/g, '<code style="background:#EDF2F7;padding:1px 4px;border-radius:3px;font-size:0.85em">$1</code>')
            .replace(/^### (.*$)/gm, '<strong style="font-size:1em;display:block;margin-top:8px">$1</strong>')
            .replace(/^## (.*$)/gm, '<strong style="font-size:1.05em;display:block;margin-top:8px">$1</strong>')
            .replace(/^# (.*$)/gm, '<strong style="font-size:1.1em;display:block;margin-top:8px">$1</strong>')
            .replace(/^- (.*$)/gm, '• $1')),
        }}
      />
    );
  });
};

const ChatMessage = ({ message }) => {
  const isUser = message.role === "user";
  const isError = message.isError;

  return (
    <Box
      alignSelf={isUser ? "flex-end" : "flex-start"}
      maxW="90%"
    >
      <Box
        bg={isUser ? "brand.500" : isError ? "red.50" : "white"}
        color={isUser ? "white" : isError ? "red.600" : "gray.800"}
        px={4}
        py={3}
        borderRadius={isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px"}
        shadow="sm"
        border={!isUser ? "1px solid" : "none"}
        borderColor="gray.100"
      >
        {isUser ? (
          <Text fontSize="sm" lineHeight="1.6">
            {message.content}
          </Text>
        ) : (
          renderContent(message.content)
        )}
      </Box>
    </Box>
  );
};

export default ChatMessage;
